/**
 * Cycle Builder Wizard – Step 1: Drag-and-drop criteria (template or scratch)
 */
import React, { useState, useEffect, useLayoutEffect, useCallback, useMemo, useRef } from "react";
import { translateUi } from '../i18n/index';
import { Plus, Trash2, MoreVertical, Lock, Users, FileText, Edit2, RotateCcw } from "../icons";
import { useCycleWizard, type WizardCriteriaItem } from "./CycleWizardStore";
import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";
import { cb, cbCn } from "./cycleBuilderTheme";
import { CycleInfoPane } from "./CycleInfoPane";
import { CustomCycleCriteriaEditor } from "./CustomCycleCriteriaEditor";
import { CycleCountsBadge } from "./CycleCountsBadge";
import { CompanyCycleWorkflowCallout } from "./CompanyCycleWorkflowCallout";
import { TemplatePickerPane, SelectedTemplateUnitsPanel, type OutlinePanelSaveState } from "./TemplateSelectPanes";
import { CompanyTemplatesPane } from "./CompanyTemplatesPane";
import { AddCustomCriteriaModal, type CustomCriteriaDraft } from "./AddCustomCriteriaModal";
import { CollapsiblePane } from "./CollapsiblePane";
import { formatCycleDurationLabel } from "./formatCycleDuration";
import {
  MIME_LIBRARY,
  MIME_ROW,
  buildTemplateLibraryKeyCounts,
  countWizardCycleStats,
  displayUnitNo,
  encodeCriteriaTypeForSave,
  activeWizardCriteriaItems,
  flattenLibraryCycleToWizardItems,
  insertCustomWizardCriteriaWithRenumber,
  isCycleFixedForCompanyView,
  isLibraryPayloadPickable,
  libraryCycleFromWizardItems,
  libraryCycleIdFromLibraryKey,
  libraryKindFromLibraryKey,
  type LibraryCycle,
  type LibraryDragPayload,
  type LibraryPayload,
} from "./cycleLibraryUi";
export type ConfigStage = "method" | "pickTemplate" | "details" | "criteria";

function cn(...inputs: Parameters<typeof clsx>) {
  return twMerge(clsx(inputs));
}

type RowPayload = { index: number };

function newWizardClientKey(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `w-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

/**
 * Reveal an element inside nested overflow containers (e.g. drop columns) without
 * calling scrollIntoView, which also scrolls the window and jumps the page away from
 * a long template list.
 */
function scrollWithinOverflowParentsOnly(element: HTMLElement, behavior: ScrollBehavior = "smooth") {
  const viewportRoot = document.scrollingElement ?? document.documentElement;
  const pad = 8;
  for (let pass = 0; pass < 5; pass++) {
    const scrollParents: HTMLElement[] = [];
    let p: HTMLElement | null = element.parentElement;
    while (p && p !== viewportRoot) {
      const style = getComputedStyle(p);
      const oy = style.overflowY;
      const yScroll = oy === "auto" || oy === "scroll" || oy === "overlay";
      if (yScroll && p.scrollHeight > p.clientHeight + 1) {
        scrollParents.push(p);
      }
      p = p.parentElement;
    }
    let anyAdjusted = false;
    for (const container of scrollParents) {
      const pr = container.getBoundingClientRect();
      const er = element.getBoundingClientRect();
      const maxScroll = Math.max(0, container.scrollHeight - container.clientHeight);
      let nextTop = container.scrollTop;
      let adjustedThis = false;
      if (er.top < pr.top + pad) {
        nextTop += er.top - pr.top - pad;
        adjustedThis = true;
      } else if (er.bottom > pr.bottom - pad) {
        nextTop += er.bottom - pr.bottom + pad;
        adjustedThis = true;
      }
      if (adjustedThis) {
        nextTop = Math.max(0, Math.min(maxScroll, nextTop));
        container.scrollTo({ top: nextTop, behavior: pass === 0 ? behavior : "auto" });
        anyAdjusted = true;
      }
    }
    if (!anyAdjusted) break;
  }
}

function getTemplateLineNoFromLibraryKey(libraryKey?: string): number | null {
  if (!libraryKey) return null;
  const parts = libraryKey.split("|");
  const raw = parts[parts.length - 1];
  const idx = Number(raw);
  if (!Number.isInteger(idx) || idx < 0) return null;
  return idx + 1;
}

export function CycleWizardStep1({
  onNext,
  onGlobalTemplateSaved,
  onMounted,
  onConfigStageChange,
  configStage: configStageProp,
  onBuildModeChange,
  buildModeHint,
  currentUserEmail: _currentUserEmail,
  companyCode,
  isSystemAdmin,
  isMobile,
  isDarkMode = false,
  pendingTemplateId,
  onPendingTemplateHandled,
  onCompanyTemplatesMounted,
  onTemplateApplied,
  editingDraftCycleId,
  onSaveDraftCriteria,
  savingDraftCriteria = false,
  companyTemplatesPaneOpen = true,
  onCompanyTemplatesPaneToggle,
  suppressBackButton = false,
}: {
  onNext: () => void;
  onGlobalTemplateSaved?: (title?: string) => void;
  onMounted?: (refresh: () => void) => void;
  /** Notify parent so the guide bar can stay in sync. */
  onConfigStageChange?: (stage: ConfigStage) => void;
  /** Optional controlled stage from the page guide bar. */
  configStage?: ConfigStage;
  onBuildModeChange?: (mode: "template" | "scratch" | null) => void;
  /** Parent-controlled build mode (avoids a blank outline before local state catches up). */
  buildModeHint?: "template" | "scratch" | null;
  currentUserEmail?: string | null;
  /** When set (e.g. system admin viewing as company), API applies company global-template visibility. */
  companyCode?: string;
  isSystemAdmin?: boolean;
  isMobile?: boolean;
  isDarkMode?: boolean;
  /** Load this library template into the wizard as a company copy (nothing global is changed). */
  pendingTemplateId?: string | null;
  onPendingTemplateHandled?: (loaded: boolean) => void;
  onCompanyTemplatesMounted?: (refresh: () => void) => void;
  onTemplateApplied?: () => void;
  /** Editing an existing draft cycle from the cycles list — unlock criteria editing. */
  editingDraftCycleId?: number | null;
  /** Persist criteria on the draft without leaving the wizard. */
  onSaveDraftCriteria?: () => Promise<boolean>;
  savingDraftCriteria?: boolean;
  companyTemplatesPaneOpen?: boolean;
  onCompanyTemplatesPaneToggle?: () => void;
  /** Parent renders Back below framework panes (e.g. company template step). */
  suppressBackButton?: boolean;
}) {
  const { state, dispatch } = useCycleWizard();
  const [library, setLibrary] = useState<LibraryCycle[]>([]);
  const [libraryLoading, setLibraryLoading] = useState(false);
  const [customDemoLabel, setCustomDemoLabel] = useState(() => state.customLabelDemonstrate ?? "");
  const [customExplainLabel, setCustomExplainLabel] = useState(() => state.customLabelExplain ?? "");
  /** null until user chooses; template = library (+ industry units for system admins); scratch = custom rows only */
  const [buildMode, setBuildMode] = useState<"template" | "scratch" | null>(null);
  const effectiveBuildMode = buildModeHint ?? buildMode;
  const [internalStage, setInternalStage] = useState<ConfigStage>("method");
  const configStage = configStageProp ?? internalStage;
  const [showRenameColumns, setShowRenameColumns] = useState(false);
  const [dragOverZone, setDragOverZone] = useState<"demonstrate" | "explain" | null>(null);
  const [rowDropHint, setRowDropHint] = useState<{ target: number; position: "before" | "after" } | null>(null);
  const [selectedLibrary, setSelectedLibrary] = useState<Record<string, LibraryPayload>>({});
  const [draggingLibraryCount, setDraggingLibraryCount] = useState(0);
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [templateSaveError, setTemplateSaveError] = useState<string | null>(null);
  const [templateSaveOk, setTemplateSaveOk] = useState<string | null>(null);
  /** Global template only: fixed = company admins cannot edit criteria; editable = they can customize in Cycle Builder. */
  const [globalTemplatePolicy, setGlobalTemplatePolicy] = useState<"fixed" | "company_editable">("fixed");
  /** Company wizard: full criteria loaded from a fixed template — columns are read-only until cleared (e.g. scratch mode). */
  const [companyFixedTemplateReadOnly, setCompanyFixedTemplateReadOnly] = useState(false);
  const [selectedFixedTemplateName, setSelectedFixedTemplateName] = useState<string | null>(null);
  const [companyTemplatesCount, setCompanyTemplatesCount] = useState<number | null>(null);
  /** Index in criteriaItems of the row most recently added (for scroll + flash). */
  const [flashIndex, setFlashIndex] = useState<number | null>(null);
  const [outlineSaveState, setOutlineSaveState] = useState<OutlinePanelSaveState>("idle");
  const [customCriteriaModalType, setCustomCriteriaModalType] = useState<"demonstrate" | "explain" | null>(null);
  const outlineSavedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const criterionCardRefs = useRef<Record<number, HTMLDivElement | null>>({});

  const fetchLibrary = useCallback(async () => {
    setLibraryLoading(true);
    try {
      const res = await fetch(`/api/cycle-library?v=${Date.now()}`, {
        cache: "no-store",
        headers: {
          ...(_currentUserEmail ? { "X-User-Email": _currentUserEmail } : {}),
          ...(companyCode?.trim()
            ? {
                "X-Company-Code": companyCode.trim(),
                "X-Cycle-Library-Company-View": "1",
              }
            : {}),
        },
      });
      if (res.ok) setLibrary(await res.json());
    } catch (err) {
      console.error("Failed to fetch library:", err);
    } finally {
      setLibraryLoading(false);
    }
  }, [_currentUserEmail, companyCode]);

  useEffect(() => {
    fetchLibrary();
  }, [fetchLibrary]);

  useEffect(() => {
    onMounted?.(fetchLibrary);
  }, [fetchLibrary, onMounted]);

  useEffect(() => {
    if (savingDraftCriteria) setOutlineSaveState("saving");
  }, [savingDraftCriteria]);

  useEffect(
    () => () => {
      if (outlineSavedTimerRef.current) clearTimeout(outlineSavedTimerRef.current);
    },
    []
  );

  const markOutlineSaved = useCallback(() => {
    setOutlineSaveState("saved");
    if (outlineSavedTimerRef.current) clearTimeout(outlineSavedTimerRef.current);
    outlineSavedTimerRef.current = setTimeout(() => setOutlineSaveState("idle"), 2500);
  }, []);

  const handleSaveDraftCriteria = useCallback(async () => {
    if (!onSaveDraftCriteria || outlineSaveState === "saving") return;
    setOutlineSaveState("saving");
    const ok = await onSaveDraftCriteria();
    if (ok) markOutlineSaved();
    else setOutlineSaveState("idle");
  }, [markOutlineSaved, onSaveDraftCriteria, outlineSaveState]);

  const showOutlineSaveButton = editingDraftCycleId != null && configStage === "criteria";
  const outlinePanelSaveState: OutlinePanelSaveState = savingDraftCriteria
    ? "saving"
    : outlineSaveState;

  const companyWizardContext = Boolean(companyCode?.trim());
  /** Saved templates appear on the pick-template step; criteria uses a single-template outline. */
  const showLibraryPane = effectiveBuildMode === "template" && !!state.selectedFrameworkTemplateId;
  const templateCriteriaEditMode =
    effectiveBuildMode === "template" &&
    !companyFixedTemplateReadOnly &&
    (editingDraftCycleId != null ||
      (companyWizardContext && Boolean(state.selectedFrameworkTemplateId)));

  const clearCompanyFixedLock = useCallback(() => {
    setCompanyFixedTemplateReadOnly(false);
    setSelectedFixedTemplateName(null);
    dispatch({ type: "SET_SELECTED_FRAMEWORK_TEMPLATE", frameworkId: undefined });
    dispatch({ type: "SET_CYCLE_INFO_LOCKED", locked: false });
  }, [dispatch]);

  useEffect(() => {
    if (buildMode === "scratch") {
      clearCompanyFixedLock();
    }
  }, [buildMode, clearCompanyFixedLock]);

  useEffect(() => {
    if (editingDraftCycleId == null) return;
    setCompanyFixedTemplateReadOnly(false);
    setBuildMode("template");
    onBuildModeChange?.("template");
  }, [editingDraftCycleId, onBuildModeChange]);

  const setConfigStage = useCallback(
    (stage: ConfigStage) => {
      if (configStageProp == null) setInternalStage(stage);
      onConfigStageChange?.(stage);
    },
    [configStageProp, onConfigStageChange]
  );

  const applyTemplateIdentity = useCallback(
    (cycle: LibraryCycle) => {
      const templateName = String(cycle.name ?? "").trim() || cycle.id;
      dispatch({ type: "SET_TITLE", title: templateName });
      const yearsRaw = Number(cycle.expiryYears ?? 2);
      const years = Number.isFinite(yearsRaw) && yearsRaw >= 1 && yearsRaw <= 10 ? Math.floor(yearsRaw) : 2;
      dispatch({ type: "SET_DURATION", value: years, unit: "years" });
      dispatch({ type: "SET_SELECTED_FRAMEWORK_TEMPLATE", frameworkId: cycle.id });
      dispatch({ type: "SET_CYCLE_INFO_LOCKED", locked: true });
      setSelectedFixedTemplateName(templateName);
    },
    [dispatch]
  );

  const applyEditableTemplateStart = useCallback(
    (cycle: LibraryCycle) => {
      const items = flattenLibraryCycleToWizardItems(cycle).map((item) => ({
        ...item,
        clientKey: newWizardClientKey(),
        element: item.libraryKey ? "" : item.element,
      }));
      dispatch({ type: "SET_CRITERIA", items });
      applyTemplateIdentity(cycle);
      setCompanyFixedTemplateReadOnly(false);
      setSelectedLibrary({});
      setDragOverZone(null);
      setRowDropHint(null);
    },
    [applyTemplateIdentity, dispatch]
  );

  const chooseAndOpenTemplate = useCallback(
    (cycle: LibraryCycle) => {
      const fixed = isCycleFixedForCompanyView(cycle, companyWizardContext);
      applyEditableTemplateStart(cycle);
      setCompanyFixedTemplateReadOnly(fixed);
      setConfigStage("criteria");
    },
    [applyEditableTemplateStart, companyWizardContext, setConfigStage]
  );

  /** "Copy into builder" from the templates pane: open the requested template once the library has loaded. */
  useEffect(() => {
    if (!pendingTemplateId || libraryLoading) return;
    const wanted = String(pendingTemplateId).trim();
    const cycle = library.find((c) => String(c.id).trim() === wanted);
    if (!cycle) {
      onPendingTemplateHandled?.(false);
      return;
    }
    setBuildMode("template");
    onBuildModeChange?.("template");
    if (!companyWizardContext) setConfigStage("pickTemplate");
    chooseAndOpenTemplate(cycle);
    onPendingTemplateHandled?.(true);
  }, [
    pendingTemplateId,
    libraryLoading,
    library,
    chooseAndOpenTemplate,
    onBuildModeChange,
    onPendingTemplateHandled,
  ]);

  const selectBuildMode = useCallback(
    (mode: "template" | "scratch") => {
      setBuildMode(mode);
      onBuildModeChange?.(mode);
      if (mode === "template") setConfigStage("pickTemplate");
      else setConfigStage("details");
    },
    [onBuildModeChange, setConfigStage]
  );

  const effectiveDemoLabel = useMemo(
    () =>
      state.useStandardLabels
        ? translateUi("Demonstrate")
        : (customDemoLabel.trim() || state.customLabelDemonstrate?.trim() || "Demonstrate"),
    [state.useStandardLabels, customDemoLabel, state.customLabelDemonstrate]
  );

  const effectiveExplainLabel = useMemo(
    () =>
      state.useStandardLabels
        ? translateUi("Explain")
        : (customExplainLabel.trim() || state.customLabelExplain?.trim() || "Explain"),
    [state.useStandardLabels, customExplainLabel, state.customLabelExplain]
  );

  /** How many cycle rows reference each template libraryKey (wizard-only). */
  const templateLibraryKeyCounts = useMemo(
    () => buildTemplateLibraryKeyCounts(state.criteriaItems),
    [state.criteriaItems]
  );

  const toggleSelectedLibrary = useCallback(
    (payload: LibraryPayload) => {
      const key = payload.libraryKey;
      if (!key) return;
      if (!isLibraryPayloadPickable(payload, library, companyWizardContext)) return;
      setSelectedLibrary((prev) => {
        if (prev[key]) {
          const next = { ...prev };
          delete next[key];
          return next;
        }
        return { ...prev, [key]: payload };
      });
    },
    [companyWizardContext, library]
  );

  const clearSelectedLibrary = useCallback((keys?: string[]) => {
    if (!keys || keys.length === 0) {
      setSelectedLibrary({});
      return;
    }
    setSelectedLibrary((prev) => {
      const next = { ...prev };
      for (const k of keys) delete next[k];
      return next;
    });
  }, []);

  const setPayloadsSelected = useCallback((payloads: LibraryPayload[], selected: boolean) => {
    setSelectedLibrary((prev) => {
      const next = { ...prev };
      for (const p of payloads) {
        if (!p.libraryKey) continue;
        if (selected) next[p.libraryKey] = p;
        else delete next[p.libraryKey];
      }
      return next;
    });
  }, []);

  const parseLibraryDragPayload = useCallback((raw: string): LibraryPayload[] => {
    const parsed = JSON.parse(raw) as LibraryPayload | LibraryDragPayload;
    if (parsed && typeof parsed === "object" && Array.isArray((parsed as LibraryDragPayload).items)) {
      return (parsed as LibraryDragPayload).items.filter(
        (p): p is LibraryPayload => !!p && typeof p.criteria === "string" && typeof p.element === "string"
      );
    }
    if (parsed && typeof parsed === "object") {
      const single = parsed as LibraryPayload;
      if (typeof single.criteria === "string" && typeof single.element === "string") return [single];
    }
    return [];
  }, []);

  const getDragLibraryItems = useCallback(
    (payload: LibraryPayload): LibraryPayload[] => {
      const key = payload.libraryKey;
      if (!key || !selectedLibrary[key]) return [payload];
      const selectedItems = Object.values(selectedLibrary);
      if (selectedItems.length === 0) return [payload];
      return selectedItems;
    },
    [selectedLibrary]
  );

  const insertLibraryPayloads = useCallback(
    (payloads: LibraryPayload[], zoneType: "demonstrate" | "explain", insertIndex: number) => {
      if (payloads.length === 0) return;
      const allowed = payloads.filter((payload) => isLibraryPayloadPickable(payload, library, companyWizardContext));
      if (allowed.length === 0) return;
      const items = [...state.criteriaItems];
      const insertAt = Math.max(0, Math.min(items.length, insertIndex));
      const rows = allowed.map((payload) => ({
        unit_no: payload.unit_no,
        element_no: payload.element_no,
        element: payload.element,
        criteria: payload.criteria,
        type: zoneType,
        status: "Competent" as const,
        ...(payload.libraryKey ? { libraryKey: payload.libraryKey } : {}),
      }));
      items.splice(insertAt, 0, ...rows);
      dispatch({ type: "SET_CRITERIA", items });
      setFlashIndex(insertAt + rows.length - 1);
      clearSelectedLibrary(
        allowed
          .map((p) => p.libraryKey)
          .filter((k): k is string => !!k)
      );
    },
    [clearSelectedLibrary, companyWizardContext, dispatch, library, state.criteriaItems]
  );

  const insertCriteriaAt = useCallback(
    (item: WizardCriteriaItem, insertAt?: number) => {
      const items = [...state.criteriaItems];
      const idx =
        typeof insertAt === "number"
          ? Math.max(0, Math.min(items.length, insertAt))
          : items.length;
      items.splice(idx, 0, item);
      dispatch({ type: "SET_CRITERIA", items });
      setFlashIndex(idx);
    },
    [dispatch, state.criteriaItems]
  );

  const removeCriteria = (index: number) => {
    if (companyFixedTemplateReadOnly) return;
    const item = state.criteriaItems[index];
    if (!item) return;
    const isCustomRow = item.wizardSource === "custom" || !item.libraryKey?.trim();
    if (templateCriteriaEditMode && !isCustomRow) {
      setCriteriaExcluded(index, true);
      return;
    }
    setFlashIndex((fi) => {
      if (fi === null) return null;
      if (index === fi) return null;
      if (index < fi) return fi - 1;
      return fi;
    });
    dispatch({ type: "REMOVE_CRITERIA", index });
    setRowDropHint((h) => {
      if (!h) return null;
      if (h.target === index) return null;
      if (h.target > index) return { ...h, target: h.target - 1 };
      return h;
    });
  };

  const setCriteriaExcluded = useCallback(
    (index: number, excluded: boolean) => {
      const items = state.criteriaItems.map((row, i) => (i === index ? { ...row, excluded } : row));
      dispatch({ type: "SET_CRITERIA", items });
    },
    [dispatch, state.criteriaItems]
  );

  const restoreCriteria = (index: number) => {
    if (companyFixedTemplateReadOnly) return;
    setCriteriaExcluded(index, false);
  };

  const removeAllInZone = useCallback(
    (zoneType: "demonstrate" | "explain") => {
      if (companyFixedTemplateReadOnly) return;
      if (templateCriteriaEditMode) {
        const next = state.criteriaItems
          .map((item) => {
            if (item.type !== zoneType) return item;
            const isCustomRow = item.wizardSource === "custom" || !item.libraryKey?.trim();
            if (isCustomRow) return null;
            return { ...item, excluded: true };
          })
          .filter((item): item is WizardCriteriaItem => item != null);
        dispatch({ type: "SET_CRITERIA", items: next });
      } else {
        const next = state.criteriaItems.filter((item) => item.type !== zoneType);
        dispatch({ type: "SET_CRITERIA", items: next });
      }
      setFlashIndex(null);
      setRowDropHint(null);
    },
    [companyFixedTemplateReadOnly, dispatch, state.criteriaItems, templateCriteriaEditMode]
  );

  const updateCriteria = (
    index: number,
    field: keyof WizardCriteriaItem,
    value: string | WizardCriteriaItem["type"]
  ) => {
    const items = [...state.criteriaItems];
    items[index] = { ...items[index], [field]: value } as WizardCriteriaItem;
    dispatch({ type: "SET_CRITERIA", items });
  };

  const appendFromLibrary = useCallback(
    (payload: LibraryPayload, type: "demonstrate" | "explain") => {
      if (!isLibraryPayloadPickable(payload, library, companyWizardContext)) return;
      const cid = libraryCycleIdFromLibraryKey(payload.libraryKey);
      const c = cid ? library.find((x) => x.id === cid) : undefined;
      if (companyFixedTemplateReadOnly && c && !isCycleFixedForCompanyView(c, companyWizardContext)) {
        clearCompanyFixedLock();
      }
      insertCriteriaAt({
        unit_no: payload.unit_no,
        element_no: payload.element_no,
        element: payload.element,
        criteria: payload.criteria,
        type,
        status: "Competent",
        ...(payload.libraryKey ? { libraryKey: payload.libraryKey } : {}),
      });
      if (payload.libraryKey) clearSelectedLibrary([payload.libraryKey]);
    },
    [
      clearCompanyFixedLock,
      clearSelectedLibrary,
      companyFixedTemplateReadOnly,
      companyWizardContext,
      insertCriteriaAt,
      library,
    ]
  );

  const appendScratchRow = useCallback(
    (type: "demonstrate" | "explain") => {
      insertCriteriaAt({
        unit_no: "",
        element_no: "",
        element: "",
        criteria: "",
        type,
        status: "Competent",
        wizardSource: "custom",
        clientKey: newWizardClientKey(),
      });
    },
    [insertCriteriaAt]
  );

  const findZoneEndInsertIndex = useCallback(
    (zoneType: "demonstrate" | "explain") => {
      let idx = state.criteriaItems.length;
      for (let i = state.criteriaItems.length - 1; i >= 0; i--) {
        if (state.criteriaItems[i].type === zoneType) {
          idx = i + 1;
          break;
        }
      }
      return idx;
    },
    [state.criteriaItems]
  );

  const addCustomCriteriaFromModal = useCallback(
    (draft: CustomCriteriaDraft) => {
      const { items, insertedIndex } = insertCustomWizardCriteriaWithRenumber(state.criteriaItems, draft, {
        clientKey: newWizardClientKey(),
      });
      dispatch({ type: "SET_CRITERIA", items });
      setFlashIndex(insertedIndex);
    },
    [dispatch, state.criteriaItems]
  );

  const addSelectedForUnit = useCallback(
    (unitPayloads: LibraryPayload[], type: "demonstrate" | "explain") => {
      const first = unitPayloads[0];
      if (first && !isLibraryPayloadPickable(first, library, companyWizardContext)) return;
      const picked = unitPayloads.filter((p) => p.libraryKey && selectedLibrary[p.libraryKey]);
      if (picked.length === 0) return;
      const cid = first?.libraryKey ? libraryCycleIdFromLibraryKey(first.libraryKey) : null;
      const c = cid ? library.find((x) => x.id === cid) : undefined;
      if (companyFixedTemplateReadOnly && c && !isCycleFixedForCompanyView(c, companyWizardContext)) {
        clearCompanyFixedLock();
      }
      const items = [...state.criteriaItems];
      const insertAt = findZoneEndInsertIndex(type);
      const rows = picked.map((p) => ({
        unit_no: p.unit_no,
        element_no: p.element_no,
        element: p.element,
        criteria: p.criteria,
        type,
        status: "Competent" as const,
        ...(p.libraryKey ? { libraryKey: p.libraryKey } : {}),
      }));
      items.splice(insertAt, 0, ...rows);
      dispatch({ type: "SET_CRITERIA", items });
      setFlashIndex(insertAt + rows.length - 1);
      clearSelectedLibrary(picked.map((p) => p.libraryKey!).filter(Boolean));
    },
    [
      clearCompanyFixedLock,
      clearSelectedLibrary,
      companyFixedTemplateReadOnly,
      companyWizardContext,
      dispatch,
      findZoneEndInsertIndex,
      library,
      selectedLibrary,
      state.criteriaItems,
    ]
  );

  const addWholeUnitAutoByType = useCallback(
    (unitPayloads: LibraryPayload[]) => {
      if (unitPayloads.length === 0) return;
      const first = unitPayloads[0];
      if (first && !isLibraryPayloadPickable(first, library, companyWizardContext)) return;
      const cid = first?.libraryKey ? libraryCycleIdFromLibraryKey(first.libraryKey) : null;
      const c = cid ? library.find((x) => x.id === cid) : undefined;
      if (companyFixedTemplateReadOnly && c && !isCycleFixedForCompanyView(c, companyWizardContext)) {
        clearCompanyFixedLock();
      }

      const demoRows = unitPayloads
        .filter((p) => libraryKindFromLibraryKey(p.libraryKey) === "demonstrate")
        .map((p) => ({
          unit_no: p.unit_no,
          element_no: p.element_no,
          element: p.element,
          criteria: p.criteria,
          type: "demonstrate" as const,
          status: "Competent" as const,
          ...(p.libraryKey ? { libraryKey: p.libraryKey } : {}),
        }));
      const explainRows = unitPayloads
        .filter((p) => libraryKindFromLibraryKey(p.libraryKey) === "explain")
        .map((p) => ({
          unit_no: p.unit_no,
          element_no: p.element_no,
          element: p.element,
          criteria: p.criteria,
          type: "explain" as const,
          status: "Competent" as const,
          ...(p.libraryKey ? { libraryKey: p.libraryKey } : {}),
        }));
      if (demoRows.length === 0 && explainRows.length === 0) return;

      const items = [...state.criteriaItems];
      const findTypeEndIndex = (type: "demonstrate" | "explain") => {
        let idx = items.length;
        for (let i = items.length - 1; i >= 0; i--) {
          if (items[i].type === type) {
            idx = i + 1;
            break;
          }
        }
        return idx;
      };

      const demoInsertAt = findTypeEndIndex("demonstrate");
      items.splice(demoInsertAt, 0, ...demoRows);
      const explainInsertAt = findTypeEndIndex("explain");
      items.splice(explainInsertAt, 0, ...explainRows);
      dispatch({ type: "SET_CRITERIA", items });
      if (explainRows.length > 0) {
        setFlashIndex(explainInsertAt + explainRows.length - 1);
      } else {
        setFlashIndex(demoInsertAt + demoRows.length - 1);
      }

      clearSelectedLibrary(
        unitPayloads
          .map((p) => p.libraryKey)
          .filter((k): k is string => !!k)
      );
    },
    [
      clearCompanyFixedLock,
      clearSelectedLibrary,
      companyFixedTemplateReadOnly,
      companyWizardContext,
      dispatch,
      library,
      state.criteriaItems,
    ]
  );

  const moveRowToIndex = useCallback(
    (fromIndex: number, insertIndex: number, zoneType: "demonstrate" | "explain") => {
      if (companyFixedTemplateReadOnly) return;
      if (fromIndex < 0 || fromIndex >= state.criteriaItems.length) return;
      const items = [...state.criteriaItems];
      const [moved] = items.splice(fromIndex, 1);
      if (!moved) return;
      moved.type = zoneType;
      let finalIndex = Math.max(0, Math.min(items.length, insertIndex));
      if (fromIndex < insertIndex) finalIndex -= 1;
      finalIndex = Math.max(0, Math.min(items.length, finalIndex));
      items.splice(finalIndex, 0, moved);
      dispatch({ type: "SET_CRITERIA", items });
      setFlashIndex(finalIndex);
    },
    [companyFixedTemplateReadOnly, dispatch, state.criteriaItems]
  );

  const getDropPositionForCard = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const midY = rect.top + rect.height / 2;
    return e.clientY < midY ? "before" : "after";
  }, []);

  const handleDropOnZone = useCallback(
    (e: React.DragEvent, zoneType: "demonstrate" | "explain") => {
      e.preventDefault();
      setDragOverZone(null);
      setRowDropHint(null);
      setDraggingLibraryCount(0);
      if (companyFixedTemplateReadOnly) return;
      const libRaw = e.dataTransfer.getData(MIME_LIBRARY);
      if (libRaw) {
        try {
          const payloads = parseLibraryDragPayload(libRaw);
          insertLibraryPayloads(payloads, zoneType, findZoneEndInsertIndex(zoneType));
        } catch {
          /* ignore */
        }
        return;
      }
      const rowRaw = e.dataTransfer.getData(MIME_ROW);
      if (rowRaw) {
        try {
          const { index } = JSON.parse(rowRaw) as RowPayload;
          moveRowToIndex(index, findZoneEndInsertIndex(zoneType), zoneType);
        } catch {
          /* ignore */
        }
      }
    },
    [
      companyFixedTemplateReadOnly,
      companyWizardContext,
      findZoneEndInsertIndex,
      insertLibraryPayloads,
      moveRowToIndex,
      parseLibraryDragPayload,
    ]
  );

  useLayoutEffect(() => {
    if (flashIndex === null) return;
    const el = criterionCardRefs.current[flashIndex];
    if (!el) return;
    requestAnimationFrame(() => {
      scrollWithinOverflowParentsOnly(el, "smooth");
    });
  }, [flashIndex, state.criteriaItems.length]);

  useEffect(() => {
    if (flashIndex === null) return;
    const t = window.setTimeout(() => setFlashIndex(null), 2000);
    return () => window.clearTimeout(t);
  }, [flashIndex]);

  const demonstrateIndices = useMemo(
    () => state.criteriaItems.map((_, i) => i).filter((i) => state.criteriaItems[i].type === "demonstrate"),
    [state.criteriaItems]
  );
  const explainIndices = useMemo(
    () => state.criteriaItems.map((_, i) => i).filter((i) => state.criteriaItems[i].type === "explain"),
    [state.criteriaItems]
  );

  const orderedDemonstrate = demonstrateIndices;
  const orderedExplain = explainIndices;

  const completeCount = useMemo(
    () => activeWizardCriteriaItems(state.criteriaItems).length,
    [state.criteriaItems]
  );
  const cycleStats = useMemo(() => countWizardCycleStats(state.criteriaItems), [state.criteriaItems]);
  const showCompanyTemplateWorkflow =
    companyWizardContext && effectiveBuildMode === "template" && Boolean(state.selectedFrameworkTemplateId);
  const selectedTemplate = useMemo(
    () => library.find((c) => String(c.id).trim() === String(state.selectedFrameworkTemplateId ?? "").trim()),
    [library, state.selectedFrameworkTemplateId]
  );
  const liveOutlineCycle = useMemo(() => {
    if (configStage !== "criteria") return null;
    if (effectiveBuildMode === "scratch") return null;
    if (!selectedTemplate) return null;
    return libraryCycleFromWizardItems(state.criteriaItems, selectedTemplate);
  }, [configStage, effectiveBuildMode, selectedTemplate, state.criteriaItems]);

  const titleOk = state.title.trim().length > 0;
  const durationOk = state.durationValue >= 1 && Number.isFinite(state.durationValue);
  const hasFixedTemplateSelection =
    companyWizardContext && !!state.selectedFrameworkTemplateId && companyFixedTemplateReadOnly;
  const hasSavableCriteria = completeCount >= 1 || hasFixedTemplateSelection;
  const step1Valid = buildMode != null && titleOk && durationOk && hasSavableCriteria;

  const detailsValid = titleOk && durationOk;
  const stageHint =
    configStage === "method" && buildMode == null
      ? translateUi("Select Use a template or Build custom.")
      : configStage === "pickTemplate" && !state.selectedFrameworkTemplateId && !companyWizardContext
        ? translateUi("Choose a template. Name and duration come with it.")
      : configStage === "details" && !detailsValid
        ? !titleOk
          ? "Enter a cycle title to continue."
          : "Set duration to at least 1."
        : configStage === "criteria" && !step1Valid
          ? companyWizardContext
            ? "Choose a template, or add at least one criterion."
            : "Add at least one complete criterion (element + text), or use a saved template."
          : null;

  const canSaveTemplate = buildMode != null && titleOk && hasSavableCriteria;

  const persistLabelsAndNext = () => {
    if (!state.useStandardLabels) {
      dispatch({
        type: "SET_LABEL_MODE",
        useStandard: false,
        demo: customDemoLabel.trim() || "Demonstrate",
        explain: customExplainLabel.trim() || "Explain",
      });
    }
    onNext();
  };

  const goBackStage = () => {
    if (configStage === "details" || configStage === "pickTemplate") setConfigStage("method");
    else if (configStage === "criteria") {
      setConfigStage(buildMode === "template" ? "pickTemplate" : "details");
    }
  };

  const goContinueStage = async () => {
    if (configStage === "method" && buildMode === "template") setConfigStage("pickTemplate");
    else if (configStage === "method" && buildMode === "scratch") setConfigStage("details");
    else if (configStage === "pickTemplate" && state.selectedFrameworkTemplateId && !companyWizardContext) {
      setConfigStage("criteria");
    }
    else if (configStage === "details" && detailsValid) setConfigStage("criteria");
    else if (configStage === "criteria" && step1Valid) {
      if (onSaveDraftCriteria) {
        setOutlineSaveState("saving");
        const ok = await onSaveDraftCriteria();
        if (!ok) {
          setOutlineSaveState("idle");
          return;
        }
        markOutlineSaved();
      }
      persistLabelsAndNext();
    }
    else if (configStage === "criteria" && buildMode === "template" && hasSavableCriteria && !titleOk) {
      setConfigStage("details");
    }
  };

  const canContinue =
    configStage === "method"
      ? buildMode != null
      : configStage === "pickTemplate"
        ? !companyWizardContext && Boolean(state.selectedFrameworkTemplateId)
      : configStage === "details"
        ? detailsValid
        : step1Valid || (buildMode === "template" && hasSavableCriteria && !titleOk);

  const handleSaveGlobalTemplate = async () => {
    if (!canSaveTemplate || savingTemplate) return;
    setTemplateSaveError(null);
    setTemplateSaveOk(null);
    setSavingTemplate(true);
    try {
      const body = {
        title: state.title.trim(),
        expiry_years: state.durationUnit === "years" ? state.durationValue : Math.ceil(state.durationValue / 12),
        criteria: activeWizardCriteriaItems(state.criteriaItems).map((c) => ({
            unit_no: c.unit_no,
            element_no: c.element_no,
            element: c.element?.trim() || c.element_no?.trim() || "Element",
            criteria: encodeCriteriaTypeForSave(c.criteria, c.type),
            type: c.type,
          })),
        userEmail: _currentUserEmail ?? undefined,
        template_editable_by_company: globalTemplatePolicy === "company_editable",
      };
      const res = await fetch("/api/cycle-library/create", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(_currentUserEmail ? { "X-User-Email": _currentUserEmail } : {}),
        },
        body: JSON.stringify(body),
      });
      const data = await res.json().catch(() => ({} as any));
      if (!res.ok) {
        const msg =
          (typeof data?.error === "string" && data.error.trim()) ||
          (typeof data?.message === "string" && data.message.trim()) ||
          "Failed to save global template";
        const detail =
          typeof data?.details === "string"
            ? data.details.trim()
            : data?.details && typeof data.details === "object"
              ? JSON.stringify(data.details)
              : "";
        setTemplateSaveError(
          `Save failed (HTTP ${res.status}): ${msg}${detail ? ` — ${detail}` : ""}`
        );
        return;
      }
      setTemplateSaveOk(`Saved "${state.title.trim()}" to Global Templates.`);
      const savedTitle = state.title.trim();
      dispatch({ type: "SET_CRITERIA", items: [] });
      setSelectedLibrary({});
      setDragOverZone(null);
      setRowDropHint(null);
      setDraggingLibraryCount(0);
      setFlashIndex(null);
      await fetchLibrary();
      onGlobalTemplateSaved?.(savedTitle);
    } catch (err: any) {
      setTemplateSaveError(`Save failed: ${err?.message || "Failed to save global template"}`);
    } finally {
      setSavingTemplate(false);
    }
  };

  const renderCriterionCard = (itemIndex: number, zoneType: "demonstrate" | "explain") => {
    const item = state.criteriaItems[itemIndex];
    if (!item) return null;
    const fromTemplate = item.wizardSource !== "custom";
    const isExcluded = Boolean(item.excluded);
    const isFlashing = flashIndex === itemIndex;
    const templateLineNo = getTemplateLineNoFromLibraryKey(item.libraryKey);
    const dropBefore = rowDropHint?.target === itemIndex && rowDropHint.position === "before";
    const dropAfter = rowDropHint?.target === itemIndex && rowDropHint.position === "after";
    const ro = companyFixedTemplateReadOnly || isExcluded;
    return (
      <div
        key={`${item.clientKey ?? "row"}-${itemIndex}`}
        ref={(el) => {
          criterionCardRefs.current[itemIndex] = el;
        }}
        draggable={!ro && !isMobile}
        onDragStart={(e) => {
          if (ro) return;
          e.dataTransfer.setData(MIME_ROW, JSON.stringify({ index: itemIndex } satisfies RowPayload));
          e.dataTransfer.effectAllowed = "move";
        }}
        onDragOver={(e) => {
          if (ro) return;
          const hasRow = e.dataTransfer.types.includes(MIME_ROW);
          const hasLib = e.dataTransfer.types.includes(MIME_LIBRARY);
          if (!hasRow && !hasLib) return;
          e.preventDefault();
          e.stopPropagation();
          const position = getDropPositionForCard(e);
          setRowDropHint({ target: itemIndex, position });
          e.dataTransfer.dropEffect = hasLib ? "copy" : "move";
        }}
        onDragLeave={() => {
          setRowDropHint((h) => (h?.target === itemIndex ? null : h));
        }}
        onDrop={(e) => {
          if (ro) return;
          e.preventDefault();
          e.stopPropagation();
          setDraggingLibraryCount(0);
          const position = getDropPositionForCard(e);
          const insertIndex = position === "before" ? itemIndex : itemIndex + 1;
          setDragOverZone(null);
          setRowDropHint(null);
          const libRaw = e.dataTransfer.getData(MIME_LIBRARY);
          if (libRaw) {
            try {
              const payloads = parseLibraryDragPayload(libRaw);
              insertLibraryPayloads(payloads, zoneType, insertIndex);
            } catch {
              /* ignore */
            }
            return;
          }
          const rowRaw = e.dataTransfer.getData(MIME_ROW);
          if (!rowRaw) return;
          try {
            const { index } = JSON.parse(rowRaw) as RowPayload;
            moveRowToIndex(index, insertIndex, zoneType);
          } catch {
            /* ignore */
          }
        }}
        className={cn(
          "rounded-lg border p-3 shadow-sm transition-[box-shadow,background-color,border-color,opacity] duration-200",
          isExcluded
            ? isDarkMode
              ? "border-slate-700 bg-slate-900/40 opacity-60"
              : "border-slate-200 bg-slate-100/80 opacity-70"
            : isFlashing
              ? cb.criterionCard(isDarkMode, true)
              : cb.criterionCard(isDarkMode, false),
          dropBefore && "shadow-[inset_0_3px_0_0_rgba(99,102,241,0.95)]",
          dropAfter && "shadow-[inset_0_-3px_0_0_rgba(99,102,241,0.95)]"
        )}
      >
        <div className="flex items-start gap-2">
          {!companyFixedTemplateReadOnly && !isExcluded && (
            <div
              className="mt-1 cursor-grab text-slate-400 active:cursor-grabbing"
              title={translateUi("Drag to another column")}
              aria-hidden
            >
              <MoreVertical className="h-4 w-4" />
            </div>
          )}
          {companyFixedTemplateReadOnly && (
            <div className="mt-1 text-slate-300" aria-hidden>
              <Lock className="h-4 w-4" />
            </div>
          )}
          <div className="min-w-0 flex-1 space-y-2">
            <div className="flex flex-wrap items-center gap-2">
              {templateLineNo !== null && (
                <span
                  className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-slate-200 text-xs font-semibold tabular-nums text-slate-800"
                  title={translateUi("Template line {{n}}", { n: templateLineNo })}
                >
                  {templateLineNo}
                </span>
              )}
              <span
                className={cn(
                  "rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase",
                  isExcluded
                    ? "bg-slate-300 text-slate-600"
                    : fromTemplate
                      ? "bg-slate-200 text-slate-700"
                      : "bg-violet-100 text-violet-800"
                )}
              >
                {isExcluded
                  ? translateUi("Removed")
                  : fromTemplate
                    ? translateUi("Template")
                    : translateUi("Manual")}
              </span>
              <span className="text-xs text-slate-500">
                U {displayUnitNo(item.unit_no)} · EL {item.element_no || "—"}
              </span>
            </div>
            {ro ? (
              <>
                <p className={cn("text-sm font-medium", isExcluded ? "text-slate-500 line-through" : "text-slate-800")}>
                  {item.element}
                </p>
                <p
                  className={cn(
                    "whitespace-pre-wrap text-sm",
                    isExcluded ? "text-slate-500 line-through" : "text-slate-700"
                  )}
                >
                  {item.criteria}
                </p>
                {isExcluded ? (
                  <p className={cbCn("text-xs", cb.bodySm(isDarkMode))}>
                    {translateUi("Removed from your cycle — restore to include in the outline again.")}
                  </p>
                ) : null}
              </>
            ) : (
              <>
                <input
                  type="text"
                  placeholder={translateUi("Element Title - Optional")}
                  value={item.element}
                  onChange={(e) => updateCriteria(itemIndex, "element", e.target.value)}
                  className="w-full rounded border border-slate-300 px-2 py-1.5 text-sm"
                />
                <textarea
                  placeholder="Criteria text"
                  value={item.criteria}
                  onChange={(e) => updateCriteria(itemIndex, "criteria", e.target.value)}
                  rows={3}
                  className="w-full resize-y rounded border border-slate-300 px-2 py-1.5 text-sm"
                />
                <div className="grid w-full grid-cols-[minmax(0,4fr)_minmax(0,1fr)] gap-2">
                  <input
                    type="text"
                    placeholder={translateUi("Unit no.")}
                    value={item.unit_no}
                    onChange={(e) => updateCriteria(itemIndex, "unit_no", e.target.value)}
                    className="min-w-0 w-full rounded border border-slate-300 px-2 py-1 text-xs"
                  />
                  <input
                    type="text"
                    placeholder={translateUi("Element no.")}
                    value={item.element_no}
                    onChange={(e) => updateCriteria(itemIndex, "element_no", e.target.value)}
                    maxLength={4}
                    className="min-w-0 w-full rounded border border-slate-300 px-2 py-1 text-xs"
                  />
                </div>
              </>
            )}
          </div>
          {!companyFixedTemplateReadOnly && (
            isExcluded ? (
              <button
                type="button"
                onClick={() => restoreCriteria(itemIndex)}
                className={cbCn(
                  "rounded p-1.5",
                  isDarkMode
                    ? "text-emerald-300 hover:bg-emerald-950/50"
                    : "text-emerald-700 hover:bg-emerald-50"
                )}
                title={translateUi("Restore to cycle")}
              >
                <RotateCcw className="h-4 w-4" />
              </button>
            ) : (
              <button
                type="button"
                onClick={() => removeCriteria(itemIndex)}
                className="rounded p-1.5 text-slate-500 hover:bg-red-50 hover:text-red-600"
                title={
                  templateCriteriaEditMode && fromTemplate && item.libraryKey
                    ? translateUi("Remove from cycle")
                    : translateUi("Remove")
                }
              >
                <Trash2 className="h-4 w-4" />
              </button>
            )
          )}
        </div>
      </div>
    );
  };

  const renderDropZone = (
    zoneType: "demonstrate" | "explain",
    title: string,
    accent: "emerald" | "amber",
    compact?: boolean
  ) => {
    const ordered = zoneType === "demonstrate" ? orderedDemonstrate : orderedExplain;
    const isOver = dragOverZone === zoneType;
    const border = cb.dropZone(isDarkMode, accent, isOver);

    return (
      <div
        className={cn(
          "flex flex-col rounded-xl border-2 border-dashed p-3 transition-colors",
          compact
            ? "min-h-[140px] lg:min-h-[160px]"
            : "min-h-[240px] md:min-h-[280px]",
          border
        )}
        onDragOver={(e) => {
          if (companyFixedTemplateReadOnly) return;
          e.preventDefault();
          e.dataTransfer.dropEffect = e.dataTransfer.types.includes(MIME_LIBRARY) ? "copy" : "move";
          setDragOverZone(zoneType);
        }}
        onDragLeave={() => setDragOverZone((z) => (z === zoneType ? null : z))}
        onDrop={(e) => handleDropOnZone(e, zoneType)}
      >
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <h4 className={cn("text-sm font-semibold", cb.dropZoneTitle(isDarkMode, accent))}>
            {title}
          </h4>
          <div className="flex items-center gap-2">
            {!companyFixedTemplateReadOnly && ordered.length > 0 && (
              <button
                type="button"
                onClick={() => removeAllInZone(zoneType)}
                className={cb.dangerBtn(isDarkMode)}
                title={
                  templateCriteriaEditMode
                    ? translateUi("Remove all {{title}} criteria from your cycle (you can restore them later)", { title })
                    : translateUi("Remove all rows in {{title}}", { title })
                }
              >
                {templateCriteriaEditMode ? translateUi("Remove all") : translateUi("Remove all")}
              </button>
            )}
            <span className={cn("text-xs", cb.bodySm(isDarkMode))}>
              {templateCriteriaEditMode
                ? `${ordered.filter((i) => !state.criteriaItems[i]?.excluded).length}/${ordered.length}`
                : ordered.length}
            </span>
          </div>
        </div>
        {(buildMode === "scratch" || isMobile) && !companyFixedTemplateReadOnly && (
          <button
            type="button"
            onClick={() => appendScratchRow(zoneType)}
            className={cn(
              "mb-3 flex w-full items-center justify-center gap-2 rounded-lg border border-dashed px-3 py-2 text-xs font-medium",
              accent === "emerald" ? cb.accentBtn(isDarkMode, "emerald") : cb.accentBtn(isDarkMode, "amber"),
              isMobile && "min-h-[44px] text-sm"
            )}
          >
            <Plus className="h-4 w-4 shrink-0" />
            {translateUi("Add blank row")}
          </button>
        )}
        <div className="flex flex-1 flex-col gap-2 overflow-y-auto">
          {ordered.length === 0 ? (
            <div className={cn("grid flex-1 place-items-center rounded-lg border px-4 py-8 text-center text-sm", cb.dropEmpty(isDarkMode))}>
              {companyFixedTemplateReadOnly
                ? "Fixed template selected"
                : showLibraryPane
                  ? translateUi("Drop criteria here, or tick items in the template library")
                  : translateUi("Add a blank row to start")}
            </div>
          ) : (
            ordered.map((idx) => renderCriterionCard(idx, zoneType))
          )}
        </div>
      </div>
    );
  };

  return (
    <>
    <div className="space-y-4">
      {configStage === "method" && (
        <section className={cb.stageCard(isDarkMode)}>
          <h3 className={cb.heading(isDarkMode, true)}>{translateUi("How do you want to build this cycle?")}</h3>
          <p className={cbCn("mt-1 text-sm", cb.body(isDarkMode))}>
            {isSystemAdmin
              ? translateUi("Pick a starting point. Build custom to create a new global template from a blank cycle.")
              : translateUi("Pick a starting point. You can change this later by going back a step.")}
          </p>
          <div className={cbCn("mt-5 grid gap-3", isMobile ? "grid-cols-1" : "sm:grid-cols-2")}>
            <button
              type="button"
              onClick={() => selectBuildMode("template")}
              className={cb.choiceCard(isDarkMode, buildMode === "template")}
            >
              <div className="flex items-start gap-3">
                <span className={cb.choiceCardIcon(isDarkMode, buildMode === "template")}>
                  <FileText className="h-5 w-5" aria-hidden />
                </span>
                <div className="min-w-0">
                  <span className={cbCn("block font-semibold", isDarkMode ? "text-slate-100" : "text-slate-900")}>
                    {translateUi("Use a template")}
                  </span>
                  <span className={cbCn("mt-1 block text-sm", cb.bodySm(isDarkMode))}>
                    {translateUi("Choose a saved template. Name and duration come with it.")}
                  </span>
                </div>
              </div>
            </button>
            <button
              type="button"
              onClick={() => selectBuildMode("scratch")}
              className={cb.choiceCard(isDarkMode, buildMode === "scratch")}
            >
              <div className="flex items-start gap-3">
                <span className={cb.choiceCardIcon(isDarkMode, buildMode === "scratch")}>
                  <Edit2 className="h-5 w-5" aria-hidden />
                </span>
                <div className="min-w-0">
                  <span className={cbCn("block font-semibold", isDarkMode ? "text-slate-100" : "text-slate-900")}>
                    {translateUi("Build custom")}
                  </span>
                  <span className={cbCn("mt-1 block text-sm", cb.bodySm(isDarkMode))}>
                    {isSystemAdmin
                      ? translateUi("Create a new global template from a blank cycle. Add your own criteria on the next step.")
                      : translateUi("Create your own criteria from a blank cycle.")}
                  </span>
                </div>
              </div>
            </button>
          </div>
        </section>
      )}

      {configStage === "pickTemplate" && companyWizardContext && (
        <CollapsiblePane
          open={companyTemplatesPaneOpen}
          onToggle={() => onCompanyTemplatesPaneToggle?.()}
          title={translateUi("Templates available to your company")}
          summary={
            companyTemplatesCount == null
              ? translateUi("Loading…")
              : companyTemplatesCount === 0
                ? translateUi("None published")
                : `${companyTemplatesCount} ${companyTemplatesCount === 1 ? translateUi("template") : translateUi("templates")}`
          }
          isDarkMode={isDarkMode}
          isMobile={isMobile}
          bareBody
          pad="sm"
        >
          <CompanyTemplatesPane
            embedded
            inWizard
            isMobile={isMobile}
            isDarkMode={isDarkMode}
            companyCode={companyCode}
            currentUserEmail={_currentUserEmail}
            onCountChange={setCompanyTemplatesCount}
            onMounted={onCompanyTemplatesMounted}
            onApplied={onTemplateApplied}
          />
        </CollapsiblePane>
      )}

      {configStage === "pickTemplate" && !companyWizardContext && (
        <section className={cb.stageCard(isDarkMode)}>
          <h3 className={cb.heading(isDarkMode, true)}>{translateUi("Choose a template")}</h3>
          <p className={cbCn("mt-1 text-sm", cb.body(isDarkMode))}>
            {translateUi("Pick one saved cycle. Its name, duration, and criteria come with it — you can review them on the next step.")}
          </p>
          <div className="mt-5">
            <TemplatePickerPane
              library={library}
              loading={libraryLoading}
              selectedId={state.selectedFrameworkTemplateId}
              companyWizardContext={companyWizardContext}
              isFixed={(cycle) => isCycleFixedForCompanyView(cycle, companyWizardContext)}
              onUseTemplate={chooseAndOpenTemplate}
              isDarkMode={isDarkMode}
              isMobile={isMobile}
            />
          </div>
        </section>
      )}
      {configStage === "details" && (
        <section className={cb.stageCard(isDarkMode)}>
          <h3 className={cb.heading(isDarkMode, true)}>{translateUi("Name your cycle")}</h3>
          <p className={cbCn("mt-1 mb-4 text-sm", cb.body(isDarkMode))}>
            {translateUi("Give it a clear title and how long it should run.")}
          </p>
          <CycleInfoPane embedded isMobile={isMobile} isDarkMode={isDarkMode} />
        </section>
      )}

      {configStage === "criteria" && (
        <div className="space-y-4">
          {showCompanyTemplateWorkflow ? (
            <CompanyCycleWorkflowCallout
              activeStep="criteria"
              isDarkMode={isDarkMode}
              cycleTitle={state.title}
              criteriaCount={cycleStats.criteria}
            />
          ) : null}
          <section className={cb.stageCard(isDarkMode)}>
            <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
              <div>
                <h3 className={cb.heading(isDarkMode, true)}>
                  {buildMode === "template"
                    ? showCompanyTemplateWorkflow && !companyFixedTemplateReadOnly
                      ? translateUi("Customise criteria for your company cycle")
                      : translateUi("Review template criteria")
                    : translateUi("Add competency criteria")}
                </h3>
                <p className={cbCn("mt-1 text-sm", cb.body(isDarkMode))}>
                  {showCompanyTemplateWorkflow && !companyFixedTemplateReadOnly
                    ? translateUi(
                        "Remove template rows with the bin — they stay listed but greyed out and can be restored. The outline on the right updates as you edit. Continue to assessment events when ready."
                      )
                    : state.selectedFrameworkTemplateId && selectedFixedTemplateName
                    ? translateUi("Using {{name}} ({{duration}}). Demonstrate and Explain are on the left; each unit and its criteria are listed on the right.", {
                        name: selectedFixedTemplateName,
                        duration: formatCycleDurationLabel(
                          state.durationUnit === "years" ? state.durationValue * 12 : state.durationValue
                        ),
                      })
                    : translateUi(
                        "Add units, then {{demo}} and {{explain}} rows. Each row has its own element number and optional title.",
                        { demo: effectiveDemoLabel, explain: effectiveExplainLabel }
                      )}
                </p>
              </div>
              <div className="flex flex-wrap items-center justify-end gap-2 text-xs">
                {draggingLibraryCount > 0 && (
                  <span className={cb.badge(isDarkMode, "sky")}>Dragging {draggingLibraryCount}</span>
                )}
                <CycleCountsBadge units={cycleStats.units} criteria={cycleStats.criteria} isDarkMode={isDarkMode} />
                <span className={cb.badge(isDarkMode, "indigo")}>{completeCount} complete</span>
              </div>
            </div>

            {buildMode === "scratch" ? (
              <CustomCycleCriteriaEditor
                items={state.criteriaItems}
                onChange={(next) => dispatch({ type: "SET_CRITERIA", items: next })}
                demoLabel={effectiveDemoLabel}
                explainLabel={effectiveExplainLabel}
                isDarkMode={isDarkMode}
                isMobile={isMobile}
              />
            ) : (
            <div
              className={cn(
                "gap-4",
                showLibraryPane && !isMobile
                  ? "lg:grid lg:grid-cols-[minmax(260px,38%)_minmax(0,1fr)] lg:items-start lg:gap-5"
                  : "flex w-full flex-col"
              )}
            >
              {isMobile && selectedTemplate && (
                <SelectedTemplateUnitsPanel
                  key={selectedTemplate.id}
                  cycle={liveOutlineCycle ?? selectedTemplate}
                  locked={companyFixedTemplateReadOnly}
                  showSaveButton={showOutlineSaveButton}
                  outlineSaveState={outlinePanelSaveState}
                  onSaveOutline={showOutlineSaveButton ? handleSaveDraftCriteria : undefined}
                  onChangeTemplate={showOutlineSaveButton ? undefined : () => setConfigStage("pickTemplate")}
                  isDarkMode={isDarkMode}
                  isMobile={isMobile}
                  liveOutline={liveOutlineCycle != null}
                  activeCriteriaItems={liveOutlineCycle ? undefined : state.criteriaItems}
                />
              )}
              <div
                className={cn(
                  "min-w-0",
                  !showLibraryPane && "w-full",
                  showLibraryPane &&
                    (isMobile
                      ? "flex flex-col"
                      : "lg:sticky lg:top-4 lg:z-20 lg:max-h-[calc(100vh-8rem)] lg:flex lg:flex-col lg:overflow-hidden")
                )}
              >
                <div
                  className={cn(
                    "grid gap-4 min-h-0",
                    showLibraryPane && isMobile && "grid-cols-1",
                    !showLibraryPane && "w-full grid-cols-1 md:grid-cols-2",
                    showLibraryPane && !isMobile && "md:grid-cols-2 lg:grid-cols-1 lg:flex-1 lg:overflow-y-auto lg:pr-1"
                  )}
                >
                  {renderDropZone("demonstrate", effectiveDemoLabel, "emerald", showLibraryPane && !isMobile)}
                  {renderDropZone("explain", effectiveExplainLabel, "amber", showLibraryPane && !isMobile)}
                </div>
              </div>

              {!isMobile && selectedTemplate && (
                <div className="min-w-0 h-full lg:sticky lg:top-4 lg:max-h-[calc(100vh-8rem)]">
                  <SelectedTemplateUnitsPanel
                    key={selectedTemplate.id}
                    cycle={liveOutlineCycle ?? selectedTemplate}
                    locked={companyFixedTemplateReadOnly}
                    showSaveButton={showOutlineSaveButton}
                    outlineSaveState={outlinePanelSaveState}
                    onSaveOutline={showOutlineSaveButton ? handleSaveDraftCriteria : undefined}
                    onChangeTemplate={showOutlineSaveButton ? undefined : () => setConfigStage("pickTemplate")}
                    isDarkMode={isDarkMode}
                    isMobile={isMobile}
                    liveOutline={liveOutlineCycle != null}
                    activeCriteriaItems={liveOutlineCycle ? undefined : state.criteriaItems}
                  />
                </div>
              )}
            </div>
            )}

            {templateCriteriaEditMode && configStage === "criteria" && (
              <div
                className={cbCn(
                  "mt-4 rounded-xl border border-dashed px-4 py-4",
                  isDarkMode ? "border-slate-600 bg-slate-900/40" : "border-slate-300 bg-slate-50/80"
                )}
              >
                <p className={cbCn("text-sm font-semibold", isDarkMode ? "text-slate-100" : "text-slate-900")}>
                  {translateUi("Add custom criteria")}
                </p>
                <p className={cbCn("mt-1 text-xs", cb.bodySm(isDarkMode))}>
                  {translateUi(
                    "Add your own Demonstrate or Explain rows alongside the template. Custom rows are deleted permanently when removed."
                  )}
                </p>
                <div className="mt-3 flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => setCustomCriteriaModalType("demonstrate")}
                    className={cbCn(
                      "inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-xs font-semibold",
                      isMobile && "min-h-[44px]",
                      cb.accentBtn(isDarkMode, "emerald")
                    )}
                  >
                    <Plus className="h-3.5 w-3.5 shrink-0" />
                    {translateUi("Custom {{label}} row", { label: effectiveDemoLabel })}
                  </button>
                  <button
                    type="button"
                    onClick={() => setCustomCriteriaModalType("explain")}
                    className={cbCn(
                      "inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-xs font-semibold",
                      isMobile && "min-h-[44px]",
                      cb.accentBtn(isDarkMode, "amber")
                    )}
                  >
                    <Plus className="h-3.5 w-3.5 shrink-0" />
                    {translateUi("Custom {{label}} row", { label: effectiveExplainLabel })}
                  </button>
                </div>
              </div>
            )}

            <div className={cbCn("mt-4 border-t pt-3", isDarkMode ? "border-slate-700" : "border-slate-200")}>
              <button
                type="button"
                onClick={() => setShowRenameColumns((v) => !v)}
                className={cbCn(
                  "text-sm font-medium underline-offset-2 hover:underline",
                  isMobile && "min-h-[44px]",
                  isDarkMode ? "text-slate-300" : "text-slate-600"
                )}
              >
                {showRenameColumns ? translateUi("Hide") : "Optional:"} rename {effectiveDemoLabel} / {effectiveExplainLabel} columns
              </button>
              {showRenameColumns && (
                <div className="mt-3 space-y-3">
                  <p className={cbCn("text-xs", cb.bodySm(isDarkMode))}>
                    {translateUi("These names only change the headings of the two criteria lists above. Most teams keep Demonstrate & Explain.")}
                  </p>
                  <div className="flex flex-wrap gap-x-6 gap-y-3">
                    <label className="flex cursor-pointer items-center gap-2.5">
                      <input
                        type="radio"
                        name="labelMode"
                        checked={state.useStandardLabels}
                        onChange={() => dispatch({ type: "SET_LABEL_MODE", useStandard: true })}
                        className={cb.radio(isDarkMode)}
                      />
                      <span className={cbCn("text-sm leading-none", cb.label(isDarkMode))}>
                        {translateUi("Keep Demonstrate & Explain")}
                      </span>
                    </label>
                    <label className="flex cursor-pointer items-center gap-2.5">
                      <input
                        type="radio"
                        name="labelMode"
                        checked={!state.useStandardLabels}
                        onChange={() => dispatch({ type: "SET_LABEL_MODE", useStandard: false })}
                        className={cb.radio(isDarkMode)}
                      />
                      <span className={cbCn("text-sm leading-none", cb.label(isDarkMode))}>{translateUi("Use my own names")}</span>
                    </label>
                  </div>
                  {!state.useStandardLabels && (
                    <div className="flex flex-wrap gap-4">
                      <div>
                        <label className={cbCn("block text-sm font-medium", cb.label(isDarkMode))}>{translateUi("First list")}</label>
                        <input
                          type="text"
                          value={customDemoLabel}
                          onChange={(e) => setCustomDemoLabel(e.target.value)}
                          placeholder={translateUi("Demonstrate")}
                          className={cbCn(
                            "mt-1 w-full max-w-[12rem] rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-1",
                            cb.input(isDarkMode)
                          )}
                        />
                      </div>
                      <div>
                        <label className={cbCn("block text-sm font-medium", cb.label(isDarkMode))}>{translateUi("Second list")}</label>
                        <input
                          type="text"
                          value={customExplainLabel}
                          onChange={(e) => setCustomExplainLabel(e.target.value)}
                          placeholder={translateUi("Explain")}
                          className={cbCn(
                            "mt-1 w-full max-w-[12rem] rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-1",
                            cb.input(isDarkMode)
                          )}
                        />
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </section>
        </div>
      )}

      <div className={isMobile ? "flex flex-col gap-2" : "flex flex-col items-stretch gap-2"}>
        {configStage === "criteria" && templateSaveError && (
          <div className={cbCn("w-full rounded-lg border px-3 py-2 text-sm", cb.alertError(isDarkMode))} role="alert">
            <p className="font-medium">{translateUi("Global template was not saved.")}</p>
            <p className="mt-0.5">{templateSaveError}</p>
          </div>
        )}
        {configStage === "criteria" && templateSaveOk && (
          <div className={cbCn("w-full rounded-lg border px-3 py-2 text-sm", cb.alertSuccess(isDarkMode))} role="status">
            <p className="font-medium">{translateUi("Global template saved.")}</p>
            <p className="mt-0.5">{templateSaveOk}</p>
          </div>
        )}
        {stageHint && (
          <p className={cbCn("text-sm", cb.hint(isDarkMode))} role="status">
            {stageHint}
          </p>
        )}
        <div
          className={cn(
            "flex gap-2",
            isMobile ? "w-full flex-col-reverse" : "flex-row flex-wrap items-center justify-between"
          )}
        >
          <div>
            {configStage !== "method" && !suppressBackButton && (
              <button
                type="button"
                onClick={goBackStage}
                className={cbCn(
                  "rounded-xl font-medium",
                  isMobile ? "w-full py-3 min-h-[48px] text-base" : "px-6 py-2.5",
                  cb.btnSecondary(isDarkMode)
                )}
              > {translateUi("Back")} </button>
            )}
          </div>
          <div className={cn("flex gap-2", isMobile ? "w-full flex-col" : "flex-row flex-wrap items-center justify-end")}>
            {configStage === "criteria" && isSystemAdmin && (
              <div className={cn("flex flex-row flex-wrap items-center gap-2 sm:gap-3", isMobile ? "w-full" : "")}>
                <div
                  className={cn(cb.policyGroup(isDarkMode), isMobile ? "min-w-0 flex-1" : "")}
                  role="group"
                  aria-label={translateUi("Who can edit this template at companies")}
                >
                  <button
                    type="button"
                    onClick={() => setGlobalTemplatePolicy("fixed")}
                    className={cn(
                      "inline-flex flex-1 items-center justify-center gap-1.5 rounded-lg px-3 py-2 text-xs font-semibold transition-colors",
                      isMobile && "min-h-[44px]",
                      cb.policyToggle(isDarkMode, globalTemplatePolicy === "fixed")
                    )}
                    title={translateUi("Company administrators cannot change criteria rows for this template")}
                  >
                    <Lock className="h-3.5 w-3.5 shrink-0" aria-hidden /> {translateUi("Fixed")} </button>
                  <button
                    type="button"
                    onClick={() => setGlobalTemplatePolicy("company_editable")}
                    className={cn(
                      "inline-flex flex-1 items-center justify-center gap-1.5 rounded-lg px-3 py-2 text-xs font-semibold transition-colors",
                      isMobile && "min-h-[44px]",
                      cb.policyToggle(isDarkMode, globalTemplatePolicy === "company_editable")
                    )}
                    title={translateUi("Company admins may edit criteria in Cycle Builder")}
                  >
                    <Users className="h-3.5 w-3.5 shrink-0" aria-hidden /> {translateUi("Editable")} </button>
                </div>
                <button
                  type="button"
                  onClick={handleSaveGlobalTemplate}
                  disabled={!canSaveTemplate || savingTemplate}
                  className={cn(
                    "rounded-xl font-medium text-white transition-colors",
                    isMobile ? "min-h-[48px] flex-1 py-3 text-base sm:flex-none sm:px-6 sm:py-2.5" : "shrink-0 px-6 py-2.5",
                    canSaveTemplate && !savingTemplate
                      ? "bg-emerald-600 hover:bg-emerald-700"
                      : "cursor-not-allowed bg-slate-400"
                  )}
                >
                  {savingTemplate ? translateUi("Saving template...") : translateUi("Save to Global Templates")}
                </button>
              </div>
            )}
            {configStage !== "method" && !(configStage === "pickTemplate" && companyWizardContext) && (
            <div className={cn("flex flex-col gap-1", isMobile ? "w-full" : "items-end")}>
              <button
                type="button"
                onClick={() => void goContinueStage()}
                disabled={!canContinue || outlinePanelSaveState === "saving"}
                className={cn(
                  "rounded-xl font-medium text-white transition-colors",
                  isMobile ? "w-full py-3 min-h-[48px] text-base" : "px-6 py-2.5",
                  canContinue
                    ? "bg-indigo-600 hover:bg-indigo-700"
                    : isDarkMode
                      ? "cursor-not-allowed bg-slate-700 text-slate-400"
                      : "cursor-not-allowed bg-slate-400"
                )}
              >
                {configStage === "criteria"
                  ? showCompanyTemplateWorkflow
                    ? translateUi("Next: schedule assessment events")
                    : translateUi("Continue to assessment events")
                  : configStage === "pickTemplate"
                    ? translateUi("Review criteria")
                    : translateUi("Continue")}
              </button>
              {configStage === "criteria" && showCompanyTemplateWorkflow && canContinue ? (
                <p className={cbCn("text-center text-xs sm:text-right", cb.bodySm(isDarkMode))}>
                  {editingDraftCycleId
                    ? outlinePanelSaveState === "saved"
                      ? translateUi("Criteria saved — continue to schedule or edit assessment events.")
                      : translateUi("Next saves your criteria automatically, then opens assessment events.")
                    : translateUi("Not saved yet — your criteria are carried forward to the next step.")}
                </p>
              ) : null}
            </div>
            )}
          </div>
        </div>
      </div>
    </div>
    <AddCustomCriteriaModal
      open={customCriteriaModalType != null}
      type={customCriteriaModalType}
      demoLabel={effectiveDemoLabel}
      explainLabel={effectiveExplainLabel}
      existingItems={state.criteriaItems}
      templateCycle={selectedTemplate ?? null}
      isDarkMode={isDarkMode}
      isMobile={isMobile}
      onClose={() => setCustomCriteriaModalType(null)}
      onAdd={addCustomCriteriaFromModal}
    />
    </>
  );
}
